import React, { useState, useEffect } from 'react';
import { useParams, Link as RouterLink, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Snackbar from '@mui/material/Snackbar';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import { useTheme } from '@mui/material/styles';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import LinkRoundedIcon from '@mui/icons-material/LinkRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import { fetchNewsArticle } from '../../services/newsService';
import { NewsArticle as ArticleType } from '../../types';

const NewsArticlePage: React.FC = () => {
    const { newsId } = useParams<{ newsId: string }>();
    const navigate = useNavigate();
    const theme = useTheme();
    const dark = theme.palette.mode === 'dark';
    const [article, setArticle] = useState<ArticleType | null>(null);
    const [loading, setLoading] = useState(true);
    const [snack, setSnack] = useState(false);

    useEffect(() => {
        if (newsId) {
            setLoading(true);
            fetchNewsArticle(newsId).then(d => {
                if (!d) { navigate('/404', { replace: true }); return; }
                setArticle(d);
                setLoading(false);
            });
        }
    }, [newsId, navigate]);

    const copyLink = () => { navigator.clipboard.writeText(window.location.href); setSnack(true); };

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 12 }}><CircularProgress /></Box>;
    if (!article) return null;

    const date = new Date(article.newsItem.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

    return (
        <Box sx={{ py: { xs: 4, md: 8 } }}>
            <Container maxWidth="md">
                <Button component={RouterLink} to="/news" startIcon={<ArrowBackRoundedIcon />} sx={{ mb: 4, color: 'text.secondary' }}>Back to News</Button>

                <Box sx={{ mb: 4 }}>
                    <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mb: 2 }}>
                        {article.newsItem.tags.map(t => <Chip key={t} label={t} size="small" sx={{ fontWeight: 600, bgcolor: dark ? 'rgba(96,165,250,0.1)' : 'rgba(37,99,235,0.06)', color: dark ? '#93C5FD' : '#2563EB' }} />)}
                    </Stack>
                    <Typography variant="h2" sx={{ fontSize: { xs: '1.8rem', md: '2.5rem' }, mb: 2, lineHeight: 1.2 }}>{article.newsItem.title}</Typography>
                    <Stack direction="row" spacing={2.5} alignItems="center" flexWrap="wrap">
                        <Stack direction="row" spacing={0.5} alignItems="center">
                            <CalendarMonthRoundedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">{date}</Typography>
                        </Stack>
                        <Tooltip title="Copy link"><IconButton onClick={copyLink} size="small" sx={{ borderRadius: '8px' }}><LinkRoundedIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                    </Stack>
                </Box>

                <Divider sx={{ mb: 4 }} />

                <Box sx={{
                    '& h1': { fontSize: '2rem', fontWeight: 800, mt: 5, mb: 2 },
                    '& h2': { fontSize: '1.5rem', fontWeight: 700, mt: 4, mb: 2 },
                    '& h3': { fontSize: '1.2rem', fontWeight: 700, mt: 3, mb: 1.5 },
                    '& p': { fontSize: '1.05rem', lineHeight: 1.8, mb: 2 },
                    '& ul, & ol': { pl: 3, mb: 2, '& li': { mb: 1, lineHeight: 1.7 } },
                    '& blockquote': { borderLeft: `4px solid ${theme.palette.primary.main}`, pl: 3, py: 1, my: 3, bgcolor: dark ? 'rgba(96,165,250,0.05)' : 'rgba(37,99,235,0.03)', borderRadius: '0 12px 12px 0', '& p': { fontStyle: 'italic', color: 'text.secondary', mb: 0 } },
                    '& code': { fontFamily: "'JetBrains Mono', monospace", fontSize: '0.85em', bgcolor: dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)', padding: '2px 6px', borderRadius: '6px' },
                    '& pre': { bgcolor: dark ? '#0D1B2A' : '#1E293B', borderRadius: '16px', p: 3, overflow: 'auto', mb: 3, border: `1px solid ${dark ? '#1E293B' : '#334155'}`, '& code': { bgcolor: 'transparent', p: 0, color: '#E2E8F0', fontSize: '0.85rem' } },
                    '& table': { width: '100%', borderCollapse: 'collapse', mb: 3, '& th, & td': { border: `1px solid ${theme.palette.divider}`, px: 2, py: 1.5, textAlign: 'left', fontSize: '0.9rem' }, '& th': { bgcolor: dark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)', fontWeight: 700 } },
                    '& hr': { border: 'none', borderTop: `1px solid ${theme.palette.divider}`, my: 4 },
                    '& a': { color: 'primary.main', textDecoration: 'none', fontWeight: 500, '&:hover': { textDecoration: 'underline' } },
                    '& strong': { fontWeight: 700 },
                }}>
                    <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
                        {article.content}
                    </ReactMarkdown>
                </Box>

                <Divider sx={{ my: 4 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Button component={RouterLink} to="/news" startIcon={<ArrowBackRoundedIcon />} sx={{ color: 'text.secondary' }}>Back to News</Button>
                    <Button onClick={copyLink} startIcon={<LinkRoundedIcon />} variant="outlined" sx={{ borderRadius: '10px' }}>Copy Link</Button>
                </Box>
            </Container>
            <Snackbar open={snack} autoHideDuration={2000} onClose={() => setSnack(false)} message="Link copied!" anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
        </Box>
    );
};

export default NewsArticlePage;
